import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { UserPlus, Pencil, Trash2, ShieldCheck, User, CheckCircle2, XCircle } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { ROLES, isSuper } from "../lib/roles";

const ROLE_STYLE = { super_admin: "bg-indigo-100 text-indigo-700", assistant: "bg-sky-100 text-sky-700", principal: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700" };

const ALL = "__all__";
const EMPTY = { name: "", phone: "", email: "", password: "", role: "pending", group_id: ALL };

export default function Admins() {
  const { t } = useLang();
  const { user } = useAuth();
  const sup = isSuper(user);
  const canDelete = (a) => a.id !== user?.id && (sup || !(["super_admin", "assistant"].includes(a.role)));
  const [admins, setAdmins] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [ra, rg] = await Promise.all([api.get("/admins"), api.get("/groups")]);
    setAdmins([...ra.data].sort((a, b) => (b.role === "pending") - (a.role === "pending")));
    setGroups(rg.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const groupName = (id) => groups.find((g) => g.id === id)?.name || "";

  const openNew = () => { setEditing(null); setForm(EMPTY); setError(""); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ name: a.name, phone: a.phone, email: a.email || "", password: "", role: a.role, group_id: a.group_id || ALL }); setError(""); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const gid = form.role !== "admin" ? "" : (form.group_id === ALL ? "" : form.group_id);
      if (editing) {
        const payload = { name: form.name, phone: form.phone, email: form.email, role: form.role, group_id: gid };
        if (form.password) payload.password = form.password;
        await api.put(`/admins/${editing.id}`, payload);
        toast.success(t("admins.updated"));
        window.dispatchEvent(new Event("pending-changed"));
      } else {
        await api.post("/admins", { name: form.name, phone: form.phone, email: form.email, password: form.password, role: form.role, group_id: gid });
        toast.success(t("admins.created"));
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(t("admins.confirmDelete"))) return;
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success(t("admins.deleted"));
      load();
      window.dispatchEvent(new Event("pending-changed"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reject = async (a) => {
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success(t("admins.rejected"));
      load();
      window.dispatchEvent(new Event("pending-changed"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const approve = async (a, role) => {
    if (role === "admin") {
      openEdit(a);
      setForm((f) => ({ ...f, role: "admin" }));
      return;
    }
    try {
      await api.put(`/admins/${a.id}`, { role });
      toast.success(t("admins.approved"));
      load();
      window.dispatchEvent(new Event("pending-changed"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground">{t("admins.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("admins.subtitle")}</p>
        </div>
        {sup && (
          <button onClick={openNew} data-testid="add-admin-btn" className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90">
            <UserPlus className="h-4 w-4" /> {t("admins.add")}
          </button>
        )}
      </div>

      <div className="surface-card overflow-hidden rounded-xl border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/60">
                <th className="px-5 py-3 text-start font-medium text-muted-foreground">{t("admins.name")}</th>
                <th className="px-5 py-3 text-start font-medium text-muted-foreground">{t("admins.phone")}</th>
                <th className="px-5 py-3 text-start font-medium text-muted-foreground">{t("admins.email")}</th>
                <th className="px-5 py-3 text-start font-medium text-muted-foreground">{t("admins.role")}</th>
                <th className="px-5 py-3 text-start font-medium text-muted-foreground">{t("admins.group")}</th>
                <th className="px-5 py-3 text-center font-medium text-muted-foreground">{t("admins.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`admin-row-${a.id}`}>
                  <td className="px-5 py-3 font-medium text-foreground">
                    {a.name} {a.id === user?.id && <span className="ms-1 text-xs text-muted-foreground">({t("admins.you")})</span>}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground" dir="ltr">{a.phone}</td>
                  <td className="px-5 py-3 text-muted-foreground" dir="ltr" data-testid={`admin-email-${a.id}`}>{a.email || "—"}</td>
                  <td className="px-5 py-3">
                    <span data-testid={`admin-role-${a.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${ROLE_STYLE[a.role] || "bg-slate-100 text-slate-700"}`}>
                      {a.role === "super_admin" ? <ShieldCheck className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                      {t(`roles.${a.role}`)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground" data-testid={`admin-group-${a.id}`}>
                    {a.role === "pending" ? "—" : a.role !== "admin" || !a.group_id ? t("admins.allGroups") : groupName(a.group_id)}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-center gap-2">
                      {sup && a.role === "pending" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`approve-admin-${a.id}`} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700">
                              <CheckCircle2 className="h-3.5 w-3.5" /> {t("admins.approve")}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {["principal", "assistant", "admin"].map((r) => (
                              <DropdownMenuItem key={r} onClick={() => approve(a, r)} data-testid={`approve-as-${r}-${a.id}`}>{t(`roles.${r}`)}</DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {sup && a.role === "pending" && (
                        <button onClick={() => reject(a)} data-testid={`reject-admin-${a.id}`} className="inline-flex items-center gap-1 rounded-lg border border-destructive/40 px-2.5 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10">
                          <XCircle className="h-3.5 w-3.5" /> {t("admins.reject")}
                        </button>
                      )}
                      {sup && (
                        <button onClick={() => openEdit(a)} data-testid={`edit-admin-${a.id}`} aria-label={t("admins.edit")}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDelete(a) && (
                        <button onClick={() => remove(a)} data-testid={`delete-admin-${a.id}`} aria-label={t("admins.delete")}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("admins.editTitle") : t("admins.newTitle")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t("admins.name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name" required />
            </div>
            <div className="space-y-2">
              <Label>{t("admins.phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-phone" dir="ltr" required={!editing} />
            </div>
            <div className="space-y-2">
              <Label>{t("admins.email")}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email" dir="ltr" placeholder={t("admins.emailPh")} />
            </div>
            <div className="space-y-2">
              <Label>{editing ? t("admins.passwordEdit") : t("admins.password")}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password" required={!editing} />
            </div>
            <div className="space-y-2">
              <Label>{t("admins.role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="admin-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`admin-role-option-${r}`}>{t(`roles.${r}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.role === "admin" && (
              <div className="space-y-2">
                <Label>{t("admins.group")}</Label>
                <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                  <SelectTrigger data-testid="admin-group"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>{t("admins.allGroups")}</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id} data-testid={`admin-group-option-${g.id}`}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {error && <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="admin-error">{error}</div>}
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary">{t("admins.cancel")}</button>
              <button type="submit" disabled={saving} data-testid="admin-save" className="brand-btn rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">{t("admins.save")}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
